if ( !customElements.get( "tabs-component" ) )
{
	class TabsComponent extends HTMLElement {
		constructor() {
			super();
			this.selectors = {
				tabHeader: '[role="tablist"]',
				tabPanels: ['[role="tabpanel"]'],
				tabNavs: ['[role="tab"]'],
				tabNav: '.tabs__nav-js',
			};
			this.domNodes = FoxTheme.utils.queryDomNodes(this.selectors, this);

			this.selectedIndex = 0;
			this.selectedTab = this.domNodes.tabPanels[this.selectedIndex];
			this.scrollToActiveTab = this.dataset.scrollToActiveTab === 'true';
			this.scrollOffset = 20;
			this.headerEl = document.querySelector('.header-section header[is="sticky-header"]');

			this.init();
			this.setActiveTab(0);

			this.tabChange = new CustomEvent('tabChange', {
				bubbles: true,
				detail: { selectedTab: this.selectedTab },
			});
		}

		connectedCallback() {
			this.setActiveTab(0);
		}

		init = () => {
			this.buttons.forEach((tab) => {
				tab.addEventListener('click', this.onTabClick.bind(this));
			});
			this.domNodes.tabHeader && this.domNodes.tabHeader.addEventListener('keydown', this.handleKeyDown.bind(this));
			this.setAccessible();
		};

		static get observedAttributes() {
			return ['data-selected'];
		}

		get buttons() {
			return (this._buttons = this._buttons || Array.from(this.domNodes.tabNavs));
		}

		getHeaderHeight() {
			let headerHeight = 0;

			if (this.headerEl) {
				const height = Math.round(this.headerEl.offsetHeight);
				if (this.headerEl.isAlwaysSticky) {
					headerHeight = height;
				} else {
					const isSticky = document.body.classList.contains(this.headerEl.classes.pinned);

					if (window.scrollY > this.offsetTop - this.scrollOffset) {
						headerHeight = height;
					}

					if (isSticky && window.scrollY >= this.offsetTop - this.scrollOffset - height) {
						headerHeight = height;
					}
				}
			}

			return headerHeight;
		}

		scrollToTop() {
			let offsetTop = this.offsetTop - this.scrollOffset - this.getHeaderHeight();
			window.scrollTo({ top: offsetTop, behavior: 'smooth' });
		}

		onTabClick(evt) {
			evt.preventDefault();
			const currentTab = evt.currentTarget;
			const index = currentTab ? Number(currentTab.dataset.index) : 0;
			this.setActiveTab(index);

			if (this.scrollToActiveTab) {
				this.scrollToTop();
			}
		}

		setActiveTab = (tabIndex) => {
			const { tabNavs, tabPanels } = this.domNodes;
			if (tabIndex !== -1) {
				const newHeader = tabNavs && tabNavs[tabIndex];
				const newTab = tabPanels && tabPanels[tabIndex];
				this.setAttribute('data-selected', tabIndex);

				tabNavs.forEach((nav) => nav.setAttribute('aria-selected', false));

				newHeader && newHeader.setAttribute('aria-selected', true);

				this.selectedIndex = tabIndex;
				this.selectedTab = newTab;

				this.dispatchEvent(
					new CustomEvent('tabChange', {
						bubbles: true,
						detail: { selectedIndex: tabIndex, selectedTab: newTab },
					})
				);
			}
		};

		setAccessible() {
			const { tabNavs, tabPanels } = this.domNodes;
			tabNavs.forEach((tab, index) => {
				if (tab.id) tabPanels[index].setAttribute('aria-labelledby', tab.id);
				tab.setAttribute('aria-selected', index === 0);
				tab.setAttribute('data-index', index);
				if (index !== 0) {
					// tab.setAttribute('tabindex', -1);
				}
			});
			tabPanels.forEach((panel, index) => {
				if (panel.id) tabNavs[index].setAttribute('aria-controls', panel.id);
				panel.setAttribute('tabindex', 0);
			});
		}

		handleKeyDown(e) {
			const { tabNavs } = this.domNodes;
			if (e.keyCode === 39 || e.keyCode === 37) {
				tabNavs[this.selectedIndex].setAttribute('tabindex', -1);
				if (e.keyCode === 39) {
					this.selectedIndex++;
					// If we're at the end, go to the start.
					if (this.selectedIndex >= tabNavs.length) {
						this.selectedIndex = 0;
					}
					// Move left.
				} else if (e.keyCode === 37) {
					this.selectedIndex--;
					// If we're at the start, move to the end.
					if (this.selectedIndex < 0) {
						this.selectedIndex = tabNavs.length - 1;
					}
				}

				tabNavs[this.selectedIndex].setAttribute('tabindex', 0);
				tabNavs[this.selectedIndex].focus();
			}
		}

		getSelected() {
			return {
				index: this.selectedIndex,
				element: this.selectedTab,
			};
		}

		attributeChangedCallback(name, oldValue, newValue) {
			if (name === 'data-selected' && oldValue !== null && oldValue !== newValue) {
				const fromButton = this.buttons[parseInt(oldValue)];
				const toButton = this.buttons[parseInt(newValue)];
				this.transition(
					document.getElementById(fromButton.getAttribute('aria-controls')),
					document.getElementById(toButton.getAttribute('aria-controls'))
				);

				this.scrollToActiveButton(toButton);
			}
		}

		scrollToActiveButton(toButton) {
			if (!this.domNodes.tabNav) return;
			const containerGap = 15;
			const scrollRect = toButton.getBoundingClientRect();
			const boxRect = this.domNodes.tabNav.getBoundingClientRect();
			const scrollLeft = this.domNodes.tabNav.scrollLeft;
			const scrollOffset = scrollRect.x + scrollLeft - boxRect.x - containerGap;

			this.domNodes.tabNav.scrollTo({
				left: scrollOffset,
				behavior: 'smooth',
			});
		}

		async transition(fromPanel, toPanel) {
			await FoxTheme.Motion.animate(
				fromPanel,
				{ transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] },
				{ duration: 0.3 }
			).finished;

			fromPanel.hidden = true;
			toPanel.hidden = false;

			FoxTheme.Motion.animate(
				toPanel,
				{ transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] },
				{ duration: 0.3 }
			).finished;

			toPanel.querySelector('grid-list') && toPanel.querySelector('grid-list').showGridItems();
		}
	}
	customElements.define('tabs-component', TabsComponent);
}